import _ from "lodash";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import useI18n from "Hooks/UseI18n";

// components
import MarkdownView from "UIComponents/MarkdownView";
import ChatAttachmentsViewer from "UIComponents/ChatAttachmentsViewer";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import TheatreModeV2 from "UIComponents/TheatreModeV2";

// design system
import { IconButton, Tag, Button } from "@toddle-edu/ds-web";
import { CloseOutlined, FlagFilled } from "@toddle-edu/ds-icons";

// third party
import classNames from "classnames";
import { graphql, compose } from "lib/apolloClient";
import { connect } from "react-redux";

// utils
import { onEnterOrSpaceKeyDown, isIdEqual } from "Utils";
import { getCommonMarkdownComponents } from "Utils/AIUtils";

// module
import {
  getAIConversationsFlaggedMessagesQuery,
  getAIConversationFlaggedMessagesQuery,
} from "./modules/AITutorFlaggedMessagesQueries";
import {
  updateAIData,
  updateEvaluationData,
} from "ClassRoom/modules/ClassRoomModule";
import { goToRelativeRoute } from "modules/Services";
import { getIsStudent } from "modules/CommonSelectors";

// scss
import classes from "./AITutorFlaggedMessages.scss";

const styles = {
  mediaWrapperContainerStyle: {
    width: "auto",
    height: "auto",
  },
};

const modes = {
  DEFAULT: "DEFAULT",
  POPOVER: "POPOVER",
  PREVIEW: "PREVIEW",
};

const AITutorFlaggedMessages = props => {
  const {
    // actual props
    mode,
    headerTitle,
    onClose,
    studentAssignmentId,

    // HOC props
    messages,
    isData = true,
    totalCount = 0,
    updateAIData,
    updateEvaluationData,
    goToRelativeRoute,
    showSensitiveContentIdsMapping,
    remainingConversations,
    lastConversationMessagesCount,
    remainingConversationMessagesCount,
  } = props;

  // hooks

  const [mediaViewerData, setMediaViewerModalState] = useState(null);
  const { t } = useI18n();

  // store attachment ids as keys with true as value for toggling sensitive content
  // {1234: true, 5678: false, 9012: true}
  useEffect(() => {
    const attachmentIds = _.flatMap(messages, ({ node }) => {
      const { attachments } = node || {};
      return _.map(attachments, "id");
    });

    const combinedAttachmentIds = _.uniq([
      ..._.keys(showSensitiveContentIdsMapping),
      ...attachmentIds,
    ]);

    const updateMapping = !_.isEqual(
      combinedAttachmentIds,
      _.keys(showSensitiveContentIdsMapping)
    );

    if (updateMapping) {
      const mapping = _.reduce(
        combinedAttachmentIds,
        (result, id) => ({
          ...result,
          [id]: showSensitiveContentIdsMapping?.[id] || true,
        }),
        {}
      );

      updateAIData({ showSensitiveContentIdsMapping: mapping });
    }
  }, [showSensitiveContentIdsMapping, messages]);

  // variables

  // if no message is flagged then return null

  const isPopoverMode = mode === modes.POPOVER;
  const isDefaultMode = mode === modes.DEFAULT;

  const markdownComponents = getCommonMarkdownComponents();

  if (totalCount === 0 && isData) return null;

  // functions

  const highlightMessageInActivityLog = () => {
    updateEvaluationData({
      selectedTab: "PRIVATE_MESSAGES",
      highlightMessageId: _.get(_.last(remainingConversations), "node.id"),
    });
  };

  const onClickMessage = ({ id }) => {
    if (isPopoverMode) {
      goToRelativeRoute({
        route: `../mediamanager/${studentAssignmentId}`,
      });
    }

    // highlight message in AI chat
    if (lastConversationMessagesCount > 0) {
      updateEvaluationData({ highlightMessageId: `${id}_AI_MESSAGE` });
    } else {
      // highlight message in activity log
      highlightMessageInActivityLog();
    }
  };

  const onClickPreviousMessages = () => {
    if (isPopoverMode) {
      goToRelativeRoute({
        route: `../mediamanager/${studentAssignmentId}`,
      });
    }

    highlightMessageInActivityLog();
  };

  const renderMediaViewer = () => {
    if (_.isEmpty(mediaViewerData)) {
      return null;
    }

    const { attachments, activeAttachmentIndex } = mediaViewerData;

    const closePreviewModal = () => {
      setMediaViewerModalState(null);
    };

    return (
      <TheatreModeV2
        mediaAttachments={{ attachments }}
        activeAttachmentIndex={activeAttachmentIndex}
        toggleTheatreMode={closePreviewModal}
      />
    );
  };

  if (
    isDefaultMode &&
    lastConversationMessagesCount === 0 &&
    remainingConversationMessagesCount > 0
  ) {
    return (
      <div className={classes.previousConversationMessagesContainer}>
        <div className={classes.iconAndTextContainer}>
          <FlagFilled variant="critical" />
          <span className={"text-body"}>
            {t("ai:flagged_messages_in_previous_conversation_with_count", {
              count: remainingConversationMessagesCount,
            })}
          </span>
        </div>

        <Button
          variant={"neutral"}
          type={"outlined"}
          dsVersion={"2.0"}
          size={"medium"}
          onClick={highlightMessageInActivityLog}
          testId={"assignment-aiTutorFlaggedMessages-view"}
        >
          {t("common:view")}
        </Button>
      </div>
    );
  }

  const renderSkeletonLoader = () => {
    const totalSkeletons = isPopoverMode ? 3 : 2;

    return _.times(totalSkeletons, index => {
      return <SkeletonLoader key={index} width={"100%"} height={"56px"} />;
    });
  };

  // classes

  const containerClass = classNames(classes.container, {
    [classes.containerViewMode]: isPopoverMode,
  });

  const headerContainerClass = classNames({
    ["text-label-l"]: !isPopoverMode,
    ["text-heading-6"]: isPopoverMode,
    [classes.headerContainer]: true,
  });

  const bottomTextClass = classNames({
    ["text-label"]: true,
    [classes.bottomText]: true,
  });

  return (
    <div className={containerClass}>
      {/* Header */}
      <div className={headerContainerClass}>
        <div className={classes.headerLeft}>
          {totalCount > 0 ? (
            headerTitle || t("common:flagged_messages")
          ) : (
            <SkeletonLoader width="160px" height="24px" />
          )}
        </div>
        <div className={classes.headerRight}>
          {isPopoverMode ? (
            <IconButton
              icon={<CloseOutlined />}
              dsVersion={"2.0"}
              variant={"neutral"}
              type={"plain"}
              size={"medium"}
              onClick={onClose}
              testId={"assignment-aiTutorFlaggedMessages-close"}
            />
          ) : totalCount == 0 ? (
            <SkeletonLoader width="42px" height="24px" />
          ) : (
            <Tag
              dsVersion={"2.0"}
              color={"pink"}
              size={"medium"}
              prefix={<FlagFilled variant="critical" />}
              testId={"assignment-aiTutorFlaggedMessages-totalCount"}
            >
              {totalCount}
            </Tag>
          )}
        </div>
      </div>

      {/* Case where the latest conversation does not contain flagged messages, but the previous one does */}
      {lastConversationMessagesCount === 0 &&
      remainingConversationMessagesCount > 0 ? (
        <div className={"text-label"}>
          {t("ai:messages_in_previous_conversation_with_count", {
            count: remainingConversationMessagesCount,
          })}
        </div>
      ) : null}

      {/* Messages */}
      {!_.isEmpty(messages)
        ? _.map(messages, ({ node }) => {
            const { id, content, attachments, metadata } = node || {};
            const { category } = metadata || {};

            const totalAttachments = _.size(attachments);

            if (_.isEmpty(_.trim(content)) && totalAttachments === 0)
              return null;

            const onClick = () => {
              onClickMessage({ id });
            };

            const onKeyDown = e => {
              onEnterOrSpaceKeyDown(e, () => onClickMessage({ id }));
            };

            const onMediaClick = selectedAttachment => {
              const activeAttachmentIndex = _.findIndex(
                attachments,
                attachment => isIdEqual(attachment.id, selectedAttachment.id)
              );

              setMediaViewerModalState({
                attachments,
                activeAttachmentIndex,
              });
            };

            const categoryClass = classNames(
              "text-label-s",
              classes.categoryLabel
            );

            return (
              <div
                key={id}
                className={classes.markdownContainer}
                onClick={onClick}
                onKeyDown={onKeyDown}
                role={"button"}
                tabIndex={0}
                aria-label={"student message"}
              >
                <div className={classes.messageWithCategory}>
                  {category ? (
                    <div className={categoryClass}>{category}</div>
                  ) : null}
                  {content ? (
                    <MarkdownView
                      content={content}
                      components={markdownComponents}
                      contentContainerClass={classes.contentContainerClass}
                    />
                  ) : null}
                </div>

                {totalAttachments ? (
                  <div className={classes.messageAttachmentContainer}>
                    <ChatAttachmentsViewer
                      totalAttachments={totalAttachments}
                      attachments={attachments}
                      onMediaClick={onMediaClick}
                      mediaWrapperContainerStyle={
                        styles.mediaWrapperContainerStyle
                      }
                      showGridView={false}
                    />
                  </div>
                ) : null}
              </div>
            );
          })
        : renderSkeletonLoader()}

      {/* Case where the latest conversation and previous conversation both contain flagged messages */}
      {lastConversationMessagesCount > 0 &&
      remainingConversationMessagesCount > 0 ? (
        <div
          className={bottomTextClass}
          role="button"
          tabIndex={0}
          aria-label="more flagged messages in previous conversation"
          onClick={onClickPreviousMessages}
          onKeyDown={e => {
            onEnterOrSpaceKeyDown(e, () => onClickPreviousMessages());
          }}
        >
          {t("ai:more_flagged_messages_in_previous_conversation_with_count", {
            count: remainingConversationMessagesCount,
          })}
        </div>
      ) : null}

      {renderMediaViewer()}
    </div>
  );
};

AITutorFlaggedMessages.propTypes = {
  mode: PropTypes.oneOf(_.values(modes)),
  onClose: PropTypes.func,
  headerTitle: PropTypes.string,
};

AITutorFlaggedMessages.defaultProps = {
  mode: "DEFAULT",
  onClose: _.noop,
  headerTitle: "",
};

const mapStateToProps = state => {
  const showSensitiveContentIdsMapping = _.get(
    state,
    "classRoom.aiData.showSensitiveContentIdsMapping"
  );

  const isStudent = getIsStudent();

  return {
    isData: true,
    isLoading: false,
    isStudent,
    showSensitiveContentIdsMapping,
  };
};

const mapActionCreators = {
  updateAIData,
  updateEvaluationData,
  goToRelativeRoute,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  // this will fetch all conversations of student from student assignment id
  // this is called from all submissions, media manager
  graphql(getAIConversationsFlaggedMessagesQuery, {
    name: "getAIConversationsFlaggedMessages",
    skip: ({ studentAssignmentId, mode, isStudent }) =>
      isStudent || mode === modes.PREVIEW || _.isEmpty(studentAssignmentId),
    options: ({ studentAssignmentId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: studentAssignmentId },
      context: {
        useHttpLink: true,
      },
    }),
    props({
      getAIConversationsFlaggedMessages,
      ownProps: { isData, isLoading },
    }) {
      const { node, networkStatus } = getAIConversationsFlaggedMessages;

      const aiConversations = _.get(node, "aiConversations.edges", []);

      const lastConversation = aiConversations?.slice(0, 1);
      const lastConversationMessages = [
        ..._.get(lastConversation, "0.node.messages.edges", []),
      ].reverse();
      const lastConversationMessagesCount = _.size(lastConversationMessages);

      const remainingConversations = aiConversations?.slice(1);
      const remainingConversationMessages = _.flatMap(
        remainingConversations,
        conversation => _.get(conversation, "node.messages.edges", [])
      );
      const remainingConversationMessagesCount = _.size(
        remainingConversationMessages
      );

      const totalCount =
        lastConversationMessagesCount + remainingConversationMessagesCount;

      const messages =
        lastConversationMessagesCount > 0
          ? lastConversationMessages
          : remainingConversationMessages;

      return {
        lastConversationMessagesCount,
        remainingConversationMessagesCount,
        remainingConversations,
        totalCount,
        messages,
        isData: isData && !_.isEmpty(node),
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  // this will fetch only one conversation of student from ai conversation id
  // this is called from assignment activity log
  graphql(getAIConversationFlaggedMessagesQuery, {
    name: "getAIConversationFlaggedMessages",
    skip: ({ aiConversationId, mode, isStudent }) =>
      isStudent || mode !== modes.PREVIEW || _.isEmpty(aiConversationId),
    options: ({ aiConversationId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: aiConversationId },
      context: {
        useHttpLink: true,
      },
    }),
    props({
      getAIConversationFlaggedMessages,
      ownProps: { isData, isLoading },
    }) {
      const { node, networkStatus } = getAIConversationFlaggedMessages;

      const lastConversationMessages = [
        ..._.get(node, "messages.edges", []),
      ].reverse();

      const lastConversationMessagesCount = _.size(lastConversationMessages);

      return {
        lastConversationMessagesCount,
        messages: lastConversationMessages,
        totalCount: lastConversationMessagesCount,
        isData: isData && !_.isEmpty(node),
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  })
)(AITutorFlaggedMessages);
